import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateMemberInput,
  Member,
  MemberOption,
  PaginatedResult,
  SortDirection,
  UpdateMemberInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface MemberListParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDir?: SortDirection;
  isActive?: boolean;
  roleId?: string;
}

const memberKeys = {
  all: ['members'] as const,
  list: (params: MemberListParams) => ['members', 'list', params] as const,
  detail: (id: string) => ['members', 'detail', id] as const,
};

/** A member counts as "online now" when last seen within this window. */
export const ONLINE_WINDOW_MS = 5 * 60_000;

/** True when the member was active within the online window. */
export function isMemberOnline(lastSeenAt: string | null): boolean {
  if (!lastSeenAt) return false;
  const seen = new Date(lastSeenAt).getTime();
  return Number.isFinite(seen) && Date.now() - seen < ONLINE_WINDOW_MS;
}

/** Active members polled for the topbar online indicator. Admin-only endpoint. */
export function useOnlineMembersPoll(enabled: boolean): UseQueryResult<PaginatedResult<Member>> {
  return useQuery({
    queryKey: ['members', 'online-poll'],
    queryFn: () =>
      api.get<PaginatedResult<Member>>(
        `/members${buildQuery({ page: 1, pageSize: 200, isActive: true })}`,
      ),
    enabled,
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
    staleTime: 15_000,
  });
}

export function useMembers(
  params: MemberListParams,
  options?: { refetchInterval?: number },
): UseQueryResult<PaginatedResult<Member>> {
  return useQuery({
    queryKey: memberKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<Member>>(
        `/members${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
          isActive: params.isActive,
          roleId: params.roleId,
        })}`,
      ),
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Minimal, non-sensitive member options for dropdowns/lookups. Authenticated-only endpoint
 * (any user) — use this instead of {@link useMembers} anywhere outside admin member
 * management so non-admins can populate sales-rep / PM / reviewer / team selects.
 */
export function useMemberOptions(): UseQueryResult<MemberOption[]> {
  return useQuery({
    queryKey: ['members', 'options'] as const,
    queryFn: () => api.get<MemberOption[]>('/members/options'),
  });
}

export function useCreateMember(): UseMutationResult<Member, unknown, CreateMemberInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMemberInput) => api.post<Member>('/members', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: memberKeys.all });
    },
  });
}

export interface UpdateMemberArgs {
  id: string;
  input: UpdateMemberInput;
}

export function useUpdateMember(): UseMutationResult<Member, unknown, UpdateMemberArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateMemberArgs) => api.patch<Member>(`/members/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: memberKeys.all });
    },
  });
}

export function useDeleteMember(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/members/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: memberKeys.all });
    },
  });
}
